/** 外框：左栏（身份 + 组织树位置 + 导航）、顶栏（组织切换 + 主题）、内容区。 */

import { useEffect, useState, type ReactNode } from 'react';
import { useSession } from '../lib/session.tsx';
import { NAV_GROUPS, visibleNav } from '../lib/nav.ts';
import { ORG_TYPE_LABEL, ROLE_LABEL, pathSegments, shortId } from '../lib/format.ts';
import { Button, Modal } from './ui.tsx';

type Theme = 'light' | 'dark' | 'system';

const THEME_KEY = 'bipay.console.theme';

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
}

function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(THEME_KEY) as Theme | null) ?? 'system',
  );
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);
  return [theme, setTheme];
}

export function Shell({
  route, onNavigate, title, children,
}: {
  route: string;
  onNavigate: (path: string) => void;
  title: string;
  children: ReactNode;
}) {
  const session = useSession();
  const { me } = session;

  /*
   * 页面名不再画在顶栏（那会和页面自己的 <h1> 重复），但它没有被浪费：
   * 拿去做浏览器标签标题。运营常常同时开着「交易」「对账」「提现审批」几个标签，
   * 标签上只写「BiPay 控制台」的话，切标签就只能靠猜。
   */
  useEffect(() => {
    document.title = `${title} · BiPay 控制台`;
  }, [title]);
  const [theme, setTheme] = useTheme();
  const [switching, setSwitching] = useState(false);
  const items = visibleNav(me.org.type, me.role, me.channel_scope !== null, me.money_hidden, me.page_scope);

  return (
    <div className="grid min-h-full grid-cols-[var(--rail-width)_1fr]">
      <aside className="sticky top-0 flex h-screen flex-col overflow-y-auto border-r border-rule bg-surface">
        <div className="flex h-[var(--topbar-height)] flex-none items-center gap-2.5 border-b border-rule px-4">
          <span className="grid size-6 flex-none place-items-center rounded bg-brand text-[12px] font-bold tracking-tight text-[var(--accent-ink)]">B</span>
          <span className="font-semibold tracking-tight">BiPay</span>
        </div>

        {/*
          组织身份（名字 + 类型 + 角色）已移到顶栏 —— 它是全局上下文，
          该常驻在视线里，而不是躺在侧栏顶部随内容一起被忽略。
          这里只留**结构**：path 就是权限模型本身，属于导航的一部分。
        */}
        {/*
          只在**真的有上级**时才画这一块。平台是根组织，path 只有自己一段，
          OrgSpine 会返回 null —— 无条件渲染标题就会留下一个底下空无一物的「组织路径」。
        */}
        {pathSegments(me.org.path).length > 1 && (
          <div className="flex flex-col gap-2 border-b border-rule px-4 py-3.5">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-faint">组织路径</div>
            <OrgSpine path={me.org.path} selfId={me.org.id} />
          </div>
        )}

        <nav className="flex flex-1 flex-col gap-px px-2 py-2.5">
          {NAV_GROUPS.map((group) => {
            const groupItems = items.filter((item) => item.group === group);
            if (groupItems.length === 0) return null;
            return (
              <div key={group}>
                <div className="px-2 pb-1.5 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-faint">{group}</div>
                {groupItems.map((item) => (
                  <button
                    key={item.path}
                    className="flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-[13.5px] text-ink-muted transition-colors hover:bg-sunk hover:text-ink aria-[current=page]:bg-brand-soft aria-[current=page]:font-semibold aria-[current=page]:text-brand"
                    {...(route === item.path ? { 'aria-current': 'page' as const } : {})}
                    onClick={() => { onNavigate(item.path); }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="flex flex-none flex-col gap-2 border-t border-rule px-3 py-2.5">
          <div className="row tight" style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
            {me.user.email}
          </div>
          <Button size="sm" onClick={session.logout}>退出登录</Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        {/*
          顶栏放**身份**，不放页面名。
          页面名每个页面自己有 <h1>，顶栏再写一遍等于同一个词在 80px 内出现两次。
          真正该常驻的是「我此刻以哪个组织、什么角色在操作」—— 在多租户平台控制台里，
          这是所有误操作的第一道防线（在代理组织下看到的数与在平台下看到的完全不同）。
        */}
        <header className="sticky top-0 z-20 flex h-[var(--topbar-height)] items-center gap-3 border-b border-rule bg-paper/90 px-5 backdrop-blur-md">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[14.5px] font-semibold tracking-tight">
              {me.memberships.find((m) => m.org_id === me.org.id)?.org_name ?? me.org.id}
            </span>
            <span className="inline-flex items-center rounded border border-ink-faint px-1.5 py-px text-[11.5px] font-semibold text-ink">{ORG_TYPE_LABEL[me.org.type]}</span>
            <span className="inline-flex items-center rounded border border-rule-strong px-1.5 py-px text-[11.5px] font-semibold text-ink-muted">{ROLE_LABEL[me.role]}</span>
          </div>
          <span className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => {
            setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark');
          }}>
            主题 · {theme === 'system' ? '跟随系统' : theme === 'dark' ? '深色' : '浅色'}
          </Button>
          {me.memberships.length > 1 && (
            <Button size="sm" onClick={() => { setSwitching(true); }}>切换组织</Button>
          )}
        </header>
        <div className="content">{children}</div>
      </div>

      {switching && (
        <Modal title="切换组织" onClose={() => { setSwitching(false); }}>
          <p className="field-hint">
            切换后当前令牌会被换成新组织的上下文，旧令牌立即失效。
          </p>
          <div className="stack tight">
            {me.memberships.map((membership) => (
              <Button
                key={membership.org_id}
                variant={membership.org_id === me.org.id ? 'primary' : undefined}
                onClick={() => {
                  void session.switchOrg(membership.org_id).then(() => { setSwitching(false); });
                }}
              >
                {membership.org_name} · {ORG_TYPE_LABEL[membership.org_type]} · {ROLE_LABEL[membership.role]}
              </Button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * 组织在树里的位置。
 *
 * `organizations.path` 不是装饰 —— 它**就是**权限模型：能看到什么由
 * `path LIKE '/平台/代理/%'` 决定。把它画出来，用户才知道自己站在哪一层、
 * 为什么有些数据看得到而有些看不到。
 */
function OrgSpine({ path, selfId }: { path: string; selfId: string }) {
  const segments = pathSegments(path);
  if (segments.length <= 1) return null;
  return (
    <div className="flex flex-col gap-0.5 text-[11px] text-ink-faint">
      {segments.map((segment, index) => (
        <span
          key={segment}
          className="flex items-center gap-1.5 overflow-hidden text-ellipsis whitespace-nowrap font-mono data-[self=true]:font-semibold data-[self=true]:text-brand"
          data-self={segment === selfId ? 'true' : 'false'}
          style={{ paddingLeft: index * 8 }}
          title={segment}
        >
          {index > 0 && <span>└</span>}
          {shortId(segment, 4)}
        </span>
      ))}
    </div>
  );
}
